import { useEditor } from '@craftjs/core';
import {
  DataBlockTypeDto,
  DataBlockVariant,
  DataBlockWithValuesDto,
  DeviceModel,
  EditUIConfigDto,
  QueryFactory,
  UIConfigType,
  useDataBlockTypeByName,
} from '@lmt/shared-types';

import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { UIEditorSaveResetControls } from '@lmt/unicorn-shared/app/components/editor/components/UIEditorSaveResetControls';
import { ToolbarComponentType } from '@lmt/unicorn-shared/app/components/editor/UIEditorTypes';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import {
  caseInformationDataBlockTypeName,
  dataBlockVariants,
  uiConfigTypes,
  useDataBlockTypeTranslation,
  visitInformationDataBlockTypeName,
} from '@lmt/unicorn-shared/app/components/measurements/common';
import { defaultUiConfig } from '@lmt/unicorn-shared/app/components/measurements/default-ui-config';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useDeviceModels } from '@lmt/unicorn-shared/app/features/mappings/operations';
import { emptyArray } from '@lmt/unicorn-shared/app/helpers/common';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useQueryParamsInTab } from '@lmt/unicorn-shared/app/helpers/use-query-params-in-tab';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useQueryClient } from '@tanstack/react-query';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { NumberParam, withDefault } from 'react-router-url-params';
import { createId } from '../../helpers/create-id';
import { CheckBox } from '../common/ui-kit/CheckBox';
import { DeveloperMode } from '../service/DeveloperMode';

import styles from './DataBlockConfig.module.scss';

type DataBlockConfigUrlParams = {
  dataBlockVariant: DataBlockVariant;
  deviceModel?: DeviceModel;
  dataBlockTypeId?: number | null;
  uiConfigType: UIConfigType;
};

export function useDataBlockConfigToolbarParams(): [
  DataBlockConfigUrlParams,
  (v: Partial<DataBlockConfigUrlParams>) => void,
] {
  return useQueryParamsInTab({
    dataBlockVariant: withDefault(
      NumberParam,
      DataBlockVariant.DeviceMeasurement,
    ),
    deviceModel: withDefault(NumberParam, DeviceModel.MiniScreen),
    dataBlockTypeId: NumberParam,
    uiConfigType: withDefault(NumberParam, UIConfigType.View),
  });
}

export const ToolbarComponent: ToolbarComponentType = React.memo((props) => {
  const i18next = useTranslation();
  const forms = useGlobalForms();
  const dispatch = useUniDispatch();
  const modals = useModal();
  const queryClient = useQueryClient();
  const [queryParams, setQueryParams] = useDataBlockConfigToolbarParams();
  const selectedUIConfigType = queryParams.uiConfigType;
  const selectedDeviceModel = queryParams.deviceModel;
  const selectedDataBlockVariant = queryParams.dataBlockVariant;
  const dataBlockTypeId = queryParams.dataBlockTypeId;
  const { query, actions } = useEditor((state, query) => ({
    enabled: state.options.enabled,
  }));
  const claims = useClaims();
  const deviceModels = useDeviceModels();

  const [isAutoCreated, setIsAutoCreated] = useState<boolean>(false);
  const [isOnlyOnePerCase, setIsOnlyOnePerCase] = useState<boolean>(false);
  const [isOnlyOnePerVisit, setIsOnlyOnePerVisit] = useState<boolean>(false);
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [showStatistic, setShowStatistic] = useState<boolean>(false);

  const dataBlockTypesQuery = QueryFactory.DataBlockTypeQuery.useGetQuery();
  const dataBlockTypes =
    dataBlockTypesQuery.data ?? emptyArray<DataBlockTypeDto>();
  const translateDataBlockType = useDataBlockTypeTranslation();

  const configQuery = QueryFactory.DataBlockTypeQuery.useGetUIConfigsQuery(
    dataBlockTypeId!,
    { enabled: !!dataBlockTypeId },
  );
  const caseInformationDataBlockType = useDataBlockTypeByName(
    caseInformationDataBlockTypeName,
  );

  const resetUndoHistory = useCallback(() => {
    actions.history.clear();
  }, []);
  useEffect(
    function resetUndoHistoryWhenConfigIsChanged() {
      resetUndoHistory();
    },
    [selectedDataBlockVariant, dataBlockTypeId, selectedUIConfigType],
  );

  const dataBlockVariantOptions = useMemo(
    () =>
      dataBlockVariants.map((variant) => {
        return {
          value: variant,
          displayValue: i18next.t(`data_block_type.${variant}`),
        };
      }),
    [],
  );

  const configOptions = useMemo(
    () =>
      uiConfigTypes.map((config) => {
        return {
          value: config,
          displayValue: i18next.t(`ui_config.${config}`),
        };
      }),
    [uiConfigTypes],
  );

  const isViewUiConfigOnly =
    selectedDataBlockVariant === DataBlockVariant.Patient;

  useEffect(() => {
    if (isViewUiConfigOnly) {
      setQueryParams({ uiConfigType: UIConfigType.View });
    }
  }, [isViewUiConfigOnly]);

  const notSelectedView = i18next
    .t(
      `ui_config.${
        selectedUIConfigType === UIConfigType.Details
          ? UIConfigType.View
          : UIConfigType.Details
      }`,
    )
    .toLowerCase();
  const filteredDataBlockTypes = useMemo(() => {
    return dataBlockTypes.filter(
      (type) =>
        type.variant === selectedDataBlockVariant &&
        !type.isDeleted &&
        !type.isDeactivated &&
        !(
          type.variant === DataBlockVariant.Visit &&
          type.value === visitInformationDataBlockTypeName
        ) &&
        (type.variant !== DataBlockVariant.DeviceMeasurement ||
          type.device === selectedDeviceModel),
    );
  }, [selectedDataBlockVariant, selectedDeviceModel, dataBlockTypes]);

  const isDataBlockTypesExist = filteredDataBlockTypes.length > 0;

  const lastHandledVariant = useRef<DataBlockVariant | undefined>(
    selectedDataBlockVariant,
  );
  useEffect(
    function selectFirstDataBlockTypeWhenVariantChanges() {
      const selectedDataBlockType = filteredDataBlockTypes.find(
        (type) => type.id === dataBlockTypeId,
      );
      if (
        lastHandledVariant.current === selectedDataBlockVariant &&
        !!selectedDataBlockType
      ) {
        setIsAutoCreated(selectedDataBlockType.isAutoCreated);
        setIsOnlyOnePerCase(selectedDataBlockType.isOnlyOnePerCase);
        setIsOnlyOnePerVisit(selectedDataBlockType.isOnlyOnePerVisit);
        // console.log(
        //   'selectFirstDataBlockTypeWhenVariantChanges.skip',
        //   selectedDataBlockType,
        // );
        // we do this because after F5 we might have data in cache (UI tests) and we need to wait for query to refetch
        return;
      }

      if (filteredDataBlockTypes?.length > 0) {
        // console.log(
        //   'selectFirstDataBlockTypeWhenVariantChanges',
        //   selectedDataBlockVariant,
        //   'lastHandledVariant:',
        //   lastHandledVariant.current,
        //   selectedDataBlockType,
        //   JSON.stringify(filteredDataBlockTypes),
        //   'query.status',
        //   dataBlockTypesQuery.status,
        // );

        if (
          !selectedDataBlockType &&
          dataBlockTypesQuery.fetchStatus === 'fetching'
        )
          return;
        const dataBlockType =
          selectedDataBlockType ?? filteredDataBlockTypes[0];

        setQueryParams({
          dataBlockTypeId: dataBlockType.id,
        });
        setIsAutoCreated(dataBlockType.isAutoCreated);
        setIsOnlyOnePerCase(dataBlockType.isOnlyOnePerCase);
        setIsOnlyOnePerVisit(dataBlockType.isOnlyOnePerVisit);
      } else {
        setQueryParams({
          dataBlockTypeId: null,
        });
      }
      lastHandledVariant.current = selectedDataBlockVariant;
    },
    [
      selectedDataBlockVariant,
      filteredDataBlockTypes,
      dataBlockTypeId,
      dataBlockTypesQuery.fetchStatus,
    ],
  );

  useEffect(
    function changeConfigSettingsIfConfigChanged() {
      if (configQuery.data) {
        setShowDetails(configQuery.data.showDetails);
        setShowStatistic(configQuery.data.showStatistic);
      }
    },
    [configQuery.data, selectedUIConfigType],
  );

  const saveConfig = useCallback(
    async (
      config: string,
      uiConfigType: UIConfigType,
      isAutoCreated: boolean,
      showDetails: boolean,
      showStatistic: boolean,
      isOnlyOnePerCase: boolean,
      isOnlyOnePerVisit: boolean,
    ) => {
      if (dataBlockTypeId) {
        props.setIsLoading(true);
        try {
          const dto: EditUIConfigDto = {
            config: config,
            type: uiConfigType,
            showDetails: showDetails,
            showStatistic: showStatistic,
            isAutoCreated: isAutoCreated,
            isOnlyOnePerCase: isOnlyOnePerCase,
            isOnlyOnePerVisit: isOnlyOnePerVisit,
          };
          await QueryFactory.DataBlockTypeClient.saveConfig(
            dataBlockTypeId,
            dto,
          );
          await queryClient.invalidateQueries({
            queryKey:
              QueryFactory.DataBlockTypeQuery.getUIConfigsQueryKey(
                dataBlockTypeId,
              ),
          });
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.DataBlockQuery.getDataBlockQueryKey(
              undefined!,
            ),
            predicate: (query) => {
              const data = query.state.data as DataBlockWithValuesDto;
              if (data && data.typeId === dataBlockTypeId) {
                return true;
              }
              return false;
            },
          });
          resetUndoHistory();
        } catch (error) {
          await modals.showError({ text: parseError(error) });
        } finally {
          props.setIsLoading(false);
        }
      }
    },
    [dataBlockTypeId],
  );

  const saveToCurrent = useCallback(
    async (config: string) => {
      await saveConfig(
        config,
        selectedUIConfigType,
        isAutoCreated,
        showDetails,
        showStatistic,
        isOnlyOnePerCase,
        isOnlyOnePerVisit,
      );
    },
    [
      selectedUIConfigType,
      dataBlockTypeId,
      isAutoCreated,
      showDetails,
      showStatistic,
      isOnlyOnePerCase,
      isOnlyOnePerVisit,
    ],
  );

  const saveToNotCurrent = useCallback(
    async (config: string) => {
      const uiConfigType =
        selectedUIConfigType === UIConfigType.Details
          ? UIConfigType.View
          : UIConfigType.Details;
      const isConfirmed = await modals.showConfirm({
        text: i18next.t('data_blocks.confirmation_copy_config', {
          view: i18next.t(`ui_config.${uiConfigType}`).toLowerCase(),
        }),
      });
      if (isConfirmed) {
        await saveConfig(
          config,
          uiConfigType,
          isAutoCreated,
          showDetails,
          showStatistic,
          isOnlyOnePerCase,
          isOnlyOnePerVisit,
        );
      }
    },
    [
      selectedUIConfigType,
      dataBlockTypeId,
      isAutoCreated,
      showDetails,
      showStatistic,
      isOnlyOnePerCase,
      isOnlyOnePerVisit,
    ],
  );

  const saveToDefaultConfig = useCallback(
    async (config: string) => {
      if (dataBlockTypeId) {
        try {
          props.setIsLoading(true);
          const dto: EditUIConfigDto = {
            config: config,
            type: selectedUIConfigType,
            showDetails: showDetails,
            showStatistic: showStatistic,
          };
          await QueryFactory.DataBlockTypeClient.saveToDefaultConfig(
            dataBlockTypeId,
            dto,
          );
        } catch (error) {
          await modals.showError({ text: parseError(error) });
        } finally {
          props.setIsLoading(false);
        }
      }
    },
    [
      dataBlockTypeId,
      isAutoCreated,
      showDetails,
      showStatistic,
      selectedUIConfigType,
      isOnlyOnePerCase,
      isOnlyOnePerVisit,
    ],
  );

  const resetConfig = useCallback(async () => {
    if (dataBlockTypeId) {
      props.setIsLoading(true);
      try {
        const uiConfigTypeDto = {
          type: selectedUIConfigType,
        };
        const config = await QueryFactory.DataBlockTypeClient.resetConfig(
          dataBlockTypeId,
          uiConfigTypeDto,
        );
        QueryFactory.DataBlockTypeQuery.setGetUIConfigsData(
          queryClient,
          // we need this `+ ' '`, otherwise rerender will not happen,
          // if default config is the same as was used when page was opened for the first time
          (data) => {
            data = data ?? {
              details: '',
              view: '',
              statistic: '',
              showDetails: false,
              showStatistic: false,
            };
            if (selectedUIConfigType === UIConfigType.View) {
              data.view = (config ?? defaultUiConfig) + ' ';
            } else {
              data.details = (config ?? defaultUiConfig) + ' ';
            }
            return data;
          },
          dataBlockTypeId,
        );
        await queryClient.invalidateQueries({
          queryKey:
            QueryFactory.DataBlockTypeQuery.getUIConfigsQueryKey(
              dataBlockTypeId,
            ),
        });
        props.setConfigKey(createId());
        resetUndoHistory();
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      } finally {
        props.setIsLoading(false);
      }
    }
  }, [dataBlockTypeId, selectedUIConfigType]);

  const isAutoCreatedChanged = useMemo(() => {
    if (!dataBlockTypeId) {
      return false;
    }
    const dataBlockType = filteredDataBlockTypes.find(
      (type) => type.id === dataBlockTypeId,
    );
    if (dataBlockType) {
      return dataBlockType.isAutoCreated !== isAutoCreated;
    } else {
      return false;
    }
  }, [dataBlockTypeId, isAutoCreated, filteredDataBlockTypes]);

  const isOnlyOnePerCaseChanged = useMemo(() => {
    if (!dataBlockTypeId) {
      return false;
    }
    const dataBlockType = filteredDataBlockTypes.find(
      (type) => type.id === dataBlockTypeId,
    );
    if (dataBlockType) {
      return dataBlockType.isOnlyOnePerCase !== isOnlyOnePerCase;
    } else {
      return false;
    }
  }, [dataBlockTypeId, isOnlyOnePerCase, filteredDataBlockTypes]);

  const isOnlyOnePerVisitChanged = useMemo(() => {
    if (!dataBlockTypeId) {
      return false;
    }
    const dataBlockType = filteredDataBlockTypes.find(
      (type) => type.id === dataBlockTypeId,
    );
    if (dataBlockType) {
      return dataBlockType.isOnlyOnePerVisit !== isOnlyOnePerVisit;
    } else {
      return false;
    }
  }, [dataBlockTypeId, isOnlyOnePerVisit, filteredDataBlockTypes]);

  const configSettingsChanged = useMemo(() => {
    if (!configQuery.data) {
      return false;
    }
    return (
      configQuery.data.showDetails !== showDetails ||
      configQuery.data.showStatistic !== showStatistic
    );
  }, [configQuery, showDetails, showStatistic]);

  const canAddDataBlockType = claims.permissions.dataBlockTypeManagement;

  return (
    <div className={styles.topBarContainer}>
      <div className={styles.settingsContainer}>
        <div className={styles.typeSelection}>
          <Field
            title={i18next.t('data_blocks.variant')}
            className={styles.filterField}
            testId="data-block-group-field"
          >
            <DropDownInput
              useIdFunctionAsValue={true}
              options={dataBlockVariantOptions}
              idFunction={'value'}
              value={selectedDataBlockVariant}
              getOptionLabel={'displayValue'}
              onValueChanged={(option) => {
                setQueryParams({
                  dataBlockVariant: option.value,
                });
              }}
              required={true}
              testId="data-block-group-dropdown"
            />
          </Field>
        </div>
        {selectedDataBlockVariant === DataBlockVariant.DeviceMeasurement && (
          <div className={styles.typeSelection}>
            <Field
              title={i18next.t('data_blocks.device_model')}
              className={styles.filterField}
              testId="data-block-group-field"
            >
              <DropDownInput
                useIdFunctionAsValue={true}
                options={deviceModels}
                idFunction={'value'}
                value={selectedDeviceModel}
                getOptionLabel={'displayValue'}
                onValueChanged={(option) => {
                  setQueryParams({
                    deviceModel: option.value,
                  });
                }}
                required={true}
              />
            </Field>
          </div>
        )}
        <div className={styles.typeSelection}>
          <Field
            title={i18next.t('data_blocks.type')}
            className={styles.filterField}
            linkProps={
              canAddDataBlockType
                ? {
                    title: i18next.t('data_blocks.add_button'),
                    onClick: () =>
                      forms.openDataBlockTypeCreation(
                        selectedDataBlockVariant,
                        selectedDataBlockVariant ===
                          DataBlockVariant.DeviceMeasurement
                          ? selectedDeviceModel
                          : undefined,
                        (dataBlockTypeId) => {
                          setQueryParams({
                            dataBlockTypeId: dataBlockTypeId,
                          });
                        },
                      ),
                  }
                : undefined
            }
            testId="data-block-config-field"
          >
            <DropDownInput
              useIdFunctionAsValue={true}
              options={filteredDataBlockTypes}
              idFunction={'id'}
              value={dataBlockTypeId}
              getOptionLabel={translateDataBlockType as any}
              onValueChanged={(dataBlockType) => {
                if (dataBlockTypes.length === 0 || !dataBlockType?.id) return;
                console.log('DataBlockType.onValueChanged', dataBlockType?.id);
                setQueryParams({
                  dataBlockTypeId: dataBlockType?.id,
                });
                setIsAutoCreated(dataBlockType.isAutoCreated);
                setIsOnlyOnePerCase(dataBlockType.isOnlyOnePerCase);
                setIsOnlyOnePerVisit(dataBlockType.isOnlyOnePerVisit);
              }}
              required={true}
              testId="data-block-config-dropdown"
            />
          </Field>
        </div>
        {isDataBlockTypesExist && !isViewUiConfigOnly && (
          <div className={styles.typeSelection}>
            <Field
              title={i18next.t('data_blocks.config')}
              className={styles.filterField}
              testId="data-block-type-field"
            >
              <DropDownInput
                useIdFunctionAsValue={true}
                options={configOptions}
                idFunction={'value'}
                value={selectedUIConfigType}
                getOptionLabel={'displayValue'}
                onValueChanged={(option) => {
                  setQueryParams({ uiConfigType: option.value });
                }}
                required={true}
                testId="data-block-type-dropdown"
              />
            </Field>
          </div>
        )}
        <DeveloperMode>
          {isDataBlockTypesExist && !!isViewUiConfigOnly && (
            <div className={styles.typeSelection}>
              <Field
                title={i18next.t('data_blocks.config')}
                className={styles.filterField}
                testId="data-block-type-field"
              >
                <DropDownInput
                  useIdFunctionAsValue={true}
                  options={configOptions}
                  idFunction={'value'}
                  value={selectedUIConfigType}
                  getOptionLabel={'displayValue'}
                  onValueChanged={(option) => {
                    setQueryParams({ uiConfigType: option.value });
                  }}
                  required={true}
                  testId="data-block-type-dropdown"
                />
              </Field>
            </div>
          )}
        </DeveloperMode>
        {isDataBlockTypesExist &&
          !isViewUiConfigOnly &&
          selectedUIConfigType === UIConfigType.Details && (
            <CheckBox
              title={i18next.t('editor.topbar.show_details')}
              name="showDetails"
              checked={showDetails}
              onChange={(e) => setShowDetails(e.currentTarget.checked)}
              className={styles.checkBox}
              testId="data-block-show-details"
            />
          )}
        {isDataBlockTypesExist &&
          !isViewUiConfigOnly &&
          selectedUIConfigType === UIConfigType.Statistic && (
            <CheckBox
              title={i18next.t('editor.topbar.show_statistic')}
              name="showStatistic"
              checked={showStatistic}
              onChange={(e) => setShowStatistic(e.currentTarget.checked)}
              className={styles.checkBox}
              testId="data-block-show-statistic"
            />
          )}
        {isDataBlockTypesExist &&
          (selectedDataBlockVariant === DataBlockVariant.Case ||
            selectedDataBlockVariant === DataBlockVariant.Visit) &&
          dataBlockTypeId !== caseInformationDataBlockType?.id && (
            <CheckBox
              title={i18next.t('editor.topbar.auto_created')}
              name="isAutoCreated"
              checked={isAutoCreated}
              onChange={(e) => setIsAutoCreated(e.currentTarget.checked)}
              className={styles.checkBox}
              testId="data-block-auto-created"
            />
          )}
        {isDataBlockTypesExist &&
          selectedDataBlockVariant !== DataBlockVariant.Patient &&
          selectedDataBlockVariant !== DataBlockVariant.Visit &&
          selectedDataBlockVariant !== DataBlockVariant.DeviceMeasurement &&
          dataBlockTypeId !== caseInformationDataBlockType?.id && (
            <CheckBox
              title={i18next.t('editor.topbar.only_one_per_case')}
              name="isOnlyOnePerCase"
              checked={isOnlyOnePerCase}
              onChange={(e) => setIsOnlyOnePerCase(e.currentTarget.checked)}
              className={styles.checkBox}
              testId="data-block-only-one-per-case"
            />
          )}
        {isDataBlockTypesExist &&
          selectedDataBlockVariant !== DataBlockVariant.Patient &&
          selectedDataBlockVariant !== DataBlockVariant.Case && (
            <CheckBox
              title={i18next.t('editor.topbar.only_one_per_visit')}
              name="isOnlyOnePerVisit"
              checked={isOnlyOnePerVisit}
              onChange={(e) => setIsOnlyOnePerVisit(e.currentTarget.checked)}
              className={styles.checkBox}
              testId="data-block-only-one-per-visit"
            />
          )}

        <DeveloperMode>
          {selectedUIConfigType !== UIConfigType.Statistic &&
            isDataBlockTypesExist && (
              <UniLink
                color={ButtonColor.Primary}
                onClick={async () => {
                  if (query.history.canUndo()) {
                    await modals.showAlert({
                      text: i18next.t(
                        'editor.topbar.confirm_message_copy_config_should_save',
                      ),
                    });
                  } else {
                    const json = query.serialize();
                    await saveToNotCurrent(json);
                  }
                }}
                title={i18next.t('editor.topbar.copy', {
                  view: notSelectedView,
                })}
                className={styles.copyLink}
              />
            )}
        </DeveloperMode>
      </div>
      {isDataBlockTypesExist && (
        <UIEditorSaveResetControls
          isShowControls={true}
          save={saveToCurrent}
          resetToDefault={resetConfig}
          saveToDefault={saveToDefaultConfig}
          isSettingsChanged={
            isAutoCreatedChanged ||
            configSettingsChanged ||
            isOnlyOnePerCaseChanged ||
            isOnlyOnePerVisitChanged
          }
        />
      )}
    </div>
  );
});
